/*
 This component expect for server-side pagination.
 Everything will be handled manually using props.
 For client-side pagination we can implement that later.
*/
import { createContext, useContext, useMemo, useState } from "react";
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  TableCellsIcon,
} from "@heroicons/react/24/solid";
import { ViewColumnsIcon } from "@heroicons/react/24/outline";
import {
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import type {
  OnChangeFn,
  PaginationState,
  ColumnDef,
  VisibilityState,
  Table as TableType,
} from "@tanstack/react-table";
import { DropdownButton } from "./DropdownButton";
import { cn } from "../utils/style";

export function EmptyState() {
  return (
    <div className="bg-gray-50 mt-4 flex h-64 items-center justify-center rounded-md border border-gray-300 border-dashed p-4">
      <div className="text-center">
        <TableCellsIcon
          className="mx-auto h-7 w-7 text-gray-300"
          aria-hidden={true}
        />
        <p className="mt-2 text-gray-800 font-medium">No data to show</p>
        <p className="text-gray-700">There are no data available</p>
      </div>
    </div>
  );
}

export type Column<TData, TValue = unknown> =
  | (ColumnDef<TData, TValue> & { defaultHidden?: undefined; id?: string })
  | (ColumnDef<TData, TValue> & { defaultHidden: true; id: string });

const DataTableContext = createContext<TableType<any> | null>(null);

export function useDataTable<TData>() {
  const context = useContext(DataTableContext);
  if (!context) {
    throw new Error("useDataTable must be used within DataTable component");
  }
  return context as TableType<TData>;
}

function ColumnToggle() {
  const table = useDataTable();

  return (
    <DropdownButton
      disabled={table.getRowCount() === 0}
      className="h-10 inline-flex items-center"
      closeOnItemClick={false}
      align="right"
      label="Columns"
      icon={<ViewColumnsIcon className="size-4" />}
      items={table.getAllLeafColumns().map((col) => ({
        label: col.columnDef.header as string | "",
        checked: col.getIsVisible(),
        onClick: () => col.toggleVisibility(!col.getIsVisible()),
      }))}
    />
  );
}

interface PaginationProps {
  pageSizes?: number[];
}

function Pagination({ pageSizes = [10, 50, 100, 500] }: PaginationProps) {
  const table = useDataTable();

  const renderButtons = (currentPage: number, totalPage: number) => {
    const size = Math.min(3, totalPage);
    const step = 2;

    const buttons: React.ReactNode[] = [];
    let start = Math.floor((currentPage - 1) / step) * step + 1;
    if (start + size - 1 > totalPage) start = totalPage - size + 1;

    for (let i = start; i < start + size; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => table.setPageIndex(i - 1)}
          className={cn(
            "text-sm h-9 min-w-10 inline-flex items-center justify-center bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors",
            currentPage === i &&
              "bg-blue-500 text-white border-blue-500 hover:bg-blue-600",
          )}
        >
          {i}
        </button>,
      );
    }
    return buttons;
  };

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-2">
      <div className="w-full flex items-center justify-between md:flex-1">
        <div className="flex items-center gap-1">
          <div className="text-sm font-medium text-gray-600">Rows per page</div>
          <select
            disabled={table.getRowCount() === 0}
            value={table.getState().pagination.pageSize}
            className="pl-2 h-9 text-sm border border-gray-200 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
            onChange={(e) => table.setPageSize(Number.parseInt(e.target.value))}
          >
            {pageSizes.map((size, i) => (
              <option key={`paginate-size-${i + 1}`} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {table.getRowCount() > 0 && (
          <div className="text-sm font-medium text-gray-600">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(
              (table.getRowCount() || 0) / table.getState().pagination.pageSize,
            )}{" "}
          </div>
        )}
      </div>

      <div className="w-full md:w-auto flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            title="Go To First"
            onClick={() => table.firstPage()}
            type="button"
            className="h-9 rounded-md px-3 text-sm font-medium border border-gray-200 text-gray-800 inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:text-gray-400 enabled:hover:bg-gray-100"
            disabled={table.getState().pagination.pageIndex === 0}
          >
            <ChevronDoubleLeftIcon className="size-4" />
          </button>
          <button
            title="Previous"
            onClick={() => table.previousPage()}
            type="button"
            className="h-9 rounded-md px-3 text-sm font-medium border border-gray-200 text-gray-800 inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:text-gray-400 enabled:hover:bg-gray-100"
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeftIcon className="size-4" />
          </button>
        </div>

        <div className="hidden md:flex items-center gap-1">
          {renderButtons(
            table.getState().pagination.pageIndex + 1,
            table.getPageCount(),
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            title="Next"
            onClick={() => table.nextPage()}
            type="button"
            className="h-9 rounded-md px-3 text-sm font-medium border border-gray-200 text-gray-800 inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:text-gray-400 enabled:hover:bg-gray-100"
            disabled={!table.getCanNextPage()}
          >
            <ChevronRightIcon className="size-4" />
          </button>

          <button
            title="Go To Last"
            onClick={() => table.lastPage()}
            type="button"
            className="h-9 rounded-md px-3 text-sm font-medium border border-gray-200 text-gray-800 inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:text-gray-400 enabled:hover:bg-gray-100"
            disabled={
              table.getRowCount() === 0 ||
              table.getState().pagination.pageIndex === table.getPageCount() - 1
            }
          >
            <ChevronDoubleRightIcon className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface DataTableProps<TData, TValue> {
  columns: Column<TData, TValue>[];
  data: TData[];
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  rowCount?: number;
  loading?: boolean;
  toolbars?: (props: { table: TableType<TData> }) => React.ReactNode;
  className?: string;
  onRowClick?: (row: Row<TData>) => void;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  pagination,
  rowCount,
  loading,
  onPaginationChange,
  toolbars,
  className,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const initialVisibility = useMemo(
    () =>
      columns.reduce(
        (acc, val) => {
          if (val.defaultHidden) {
            acc[val.id] = false;
          }
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [columns],
  );

  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(initialVisibility);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(pagination
      ? {
          manualPagination: true,
          onPaginationChange: onPaginationChange,
          rowCount: rowCount,
          state: {
            pagination,
            columnVisibility,
          },
          onColumnVisibilityChange: setColumnVisibility,
        }
      : {
          state: { columnVisibility },
          onColumnVisibilityChange: setColumnVisibility,
        }),
  });

  if (!loading && data.length === 0) {
    return (
      <DataTableContext.Provider value={table}>
        {toolbars ? toolbars({ table }) : null}
        <div className="px-4 pb-4">
          <EmptyState />
        </div>
      </DataTableContext.Provider>
    );
  }

  return (
    <DataTableContext.Provider value={table}>
      {toolbars ? toolbars({ table }) : null}
      <div
        className={cn(
          "overflow-hidden border border-gray-200 rounded-md",
          className,
        )}
      >
        <div className="relative w-full overflow-x-auto">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b bg-gray-100">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr className="border-b" key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                      key={header.id}
                      style={{
                        width: header.column.columnDef.size,
                        minWidth: header.column.columnDef.minSize,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <tr key={`pl-${i + 1}`}>
                      {columns.map((col, i) => (
                        <td key={`plc=${i + 1}`} className="w-full py-2 px-1">
                          <div className="h-8 w-full rounded-md bg-gray-100 animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      "hover:bg-gray-50 transition-colors",
                      onRowClick && "cursor-pointer",
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`px-4 py-2 text-sm text-gray-900`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="">
                  <td colSpan={columns.length} className="h-24 text-center">
                    No Results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pt-2 px-4 pb-4">
        <Pagination />
      </div>
    </DataTableContext.Provider>
  );
}

DataTable.ColumnToggle = ColumnToggle;
DataTable.Pagination = Pagination;
